import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'tss-react/mui';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Footer from '../components/footer/Footer';
import FooterSpacer from '../components/footer/FooterSpacer';
import logo from '../static/img/logo.webp';

// Note: ErrorFallback must NOT depend on React Router (Link, NavBar, etc.) because it renders
// when ErrorBoundary catches an error — at which point RouterProvider (inside App) is unmounted.
// Use plain <a href="/"> anchors and avoid any router hooks or components.

const styles = theme => ({
  staticHeader: {
    padding: theme.spacing(1, 2),
    borderBottom: '1px solid rgba(0,0,0,0.12)',
  },
  headerLogo: {
    height: 40,
    display: 'block',
  },
  appBarSpacer: theme.mixins.toolbar,
  page: {
    position: 'relative',
    width: '100%',
    minHeight: '100vh',
  },
  container: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(10),
    [theme.breakpoints.up(432)]: {
      width: 400,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  header: {
    textAlign: 'center',
    margin: theme.spacing(5),
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    height: 60,
    marginBottom: theme.spacing(4),
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    textAlign: 'center',
  },
  button: {
    marginTop: theme.spacing(2),
  },
  devDetails: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    width: '100%',
    overflowX: 'auto',
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
});

function ErrorFallback({ classes, error, componentStack, showDevDetails }) {
  const isDev = showDevDetails !== undefined ? showDevDetails : process.env.NODE_ENV === 'development';
  return (
    <div className={classes.page}>
      <header className={classes.staticHeader}>
        <a href="/"><img src={logo} alt="Nia" className={classes.headerLogo} /></a>
      </header>
      <main className={classes.container}>
        <div className={classes.appBarSpacer} />
        <Typography className={classes.header} color="textSecondary" variant="h4">
          Oops! Something went wrong.
        </Typography>
        <Paper className={classes.paper} elevation={1}>
          <img src={logo} alt="Nia" className={classes.logo} />
          <Grid justifyContent="center" container spacing={3}>
            <Typography className={classes.subtitle} variant="subtitle1">
              An unexpected error occurred. Please try again or return home.
            </Typography>
            <Button
              className={classes.button}
              component="a"
              href="/"
              variant="contained"
              color="secondary"
            >
              Go to Home
            </Button>
            {isDev && error && (
              <pre className={classes.devDetails}>
                {error.toString()}
                {componentStack && `\n\nComponent Stack:${componentStack}`}
              </pre>
            )}
          </Grid>
        </Paper>
        <FooterSpacer />
      </main>
      <Footer />
    </div>
  );
}

ErrorFallback.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.instanceOf(Error),
  componentStack: PropTypes.string,
  showDevDetails: PropTypes.bool,
};

ErrorFallback.defaultProps = {
  error: null,
  componentStack: null,
  showDevDetails: undefined,
};

export default withStyles(ErrorFallback, styles);
